import { socialAPI } from '@api/jooicez-backend/social';
import { IFindAllBrands, IFindOneBrand, IPost } from '@models/social/response';
import { _brandEmptyValues } from '@models/social/social';
import { useMutation, useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { createContext, Dispatch, SetStateAction, ReactNode, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BooleanUtil, emptyBooleanUtil, useBoolean } from 'src/hooks/use-boolean';
import localStorageKeys from 'src/utils/localStorageKeys';

import { useApp } from './ApplicationContext';
import { usePolicy } from './PolicyContext';

interface ISocialContext {
  setBrandId: Dispatch<SetStateAction<number | undefined>>;
  currentBrand: IFindOneBrand | undefined;
  currentBrandId: number | undefined;
  brands: IFindAllBrands[];
  drawerHandler: BooleanUtil;
  drawerInitialValues: IFindAllBrands;
  setDrawerInitialValues: Dispatch<SetStateAction<IFindAllBrands>>;
  refetchAllBrandsAndSet: (toSetBrand?: number) => void;
  refetchOneBrand: VoidFunction;
  isFetchingBrands: boolean;
  isFetchingBrandsSuccess: boolean;
  isFetchingOneBrand: boolean;
  openDetailsDrawer: (post: IPost) => void;
  detailDrawerHandler: BooleanUtil;
  detailPost: IPost | undefined;
  refetchDetailsPost: (postId: number) => void;

  // Internal method to enable queries
  enableContext: VoidFunction;
}

const SocialContext = createContext<ISocialContext>({
  currentBrand: undefined,
  setBrandId: () => {},
  drawerHandler: emptyBooleanUtil,
  currentBrandId: undefined,
  brands: [],
  drawerInitialValues: _brandEmptyValues,
  setDrawerInitialValues: () => {},
  refetchAllBrandsAndSet: () => {},
  refetchOneBrand: () => {},
  isFetchingBrands: false,
  isFetchingBrandsSuccess: false,
  isFetchingOneBrand: false,
  openDetailsDrawer: () => {},
  detailDrawerHandler: emptyBooleanUtil,
  detailPost: undefined,
  refetchDetailsPost: () => {},

  // Internal method to enable queries
  enableContext: () => {},
});

export const SocialContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [enable, setEnable] = useState(false);
  const enableContext = () => setEnable(true);

  const { organizationId } = useApp();
  const { hasSocial } = usePolicy();
  const [currentBrandId, setBrandId] = useState<number>();
  const [currentBrand, setCurrentBrand] = useState<IFindOneBrand>();
  const [drawerInitialValues, setDrawerInitialValues] = useState<IFindAllBrands>(_brandEmptyValues);
  const [brands, setBrands] = useState<IFindAllBrands[]>([]);
  const drawerHandler = useBoolean(false);
  const detailDrawerHandler = useBoolean(false);
  const [detailPost, setDetailPost] = useState<IPost>();
  const { t } = useTranslation('social', { keyPrefix: 'brands.context' });

  const [shouldSwitchToBrand, setShouldSwitchToBrand] = useState<number>();
  const [previousFetchedBrand, setPreviousFetchedBrand] = useState<number>();

  const [prevOrganizationId, setPrevOrganizationId] = useState(organizationId);
  if (organizationId !== prevOrganizationId) {
    setPrevOrganizationId(organizationId);
    setBrandId(undefined);
    setCurrentBrand(undefined);
    setPreviousFetchedBrand(undefined);
    setShouldSwitchToBrand(undefined);
    setBrands([]);
  }

  const {
    data: brandsData,
    isFetching: isFetchingBrands,
    refetch: refetchAllBrands,
    isSuccess: isBrandsSuccess,
    isError: isBrandsError,
  } = useQuery({
    queryKey: ['findBrand', organizationId],
    queryFn: () => socialAPI.brands.findAll(organizationId ?? 0),
    enabled: !!organizationId && (!!hasSocial || window.location.pathname.includes('/spartacus/')) && enable,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isBrandsError) {
      setBrands([]);
      setBrandId(undefined);
      return;
    }
    if (!brandsData) return;
    if (!brandsData.data || brandsData.data.length === 0) {
      setBrands([]);
      return setBrandId(undefined);
    }
    setBrands(brandsData.data);
    // Setting `shouldSwitchToBrand` re-runs this effect on the *stale* list, before the refetch
    // brings the just-created brand: keep it pending until the list actually contains it,
    // otherwise it would be consumed here and the previous brand would win afterwards.
    if (shouldSwitchToBrand && !brandsData.data.some((x: IFindAllBrands) => x.id === shouldSwitchToBrand)) return;
    const newBrandId =
      shouldSwitchToBrand || Number(localStorage.getItem(`${localStorageKeys.social.brandId}.${organizationId}`));
    if (shouldSwitchToBrand) {
      // Clearing it re-runs this effect, which then reads the id back from localStorage: persist
      // it now (the findOne effect only does it once the brand is loaded) or the previous brand
      // would be restored.
      localStorage.setItem(`${localStorageKeys.social.brandId}.${organizationId}`, `${shouldSwitchToBrand}`);
      setShouldSwitchToBrand(undefined);
    }
    const newBrandExists = brandsData.data.map((x: IFindAllBrands) => x.id).includes(newBrandId);
    if (!newBrandId || !newBrandExists) return setBrandId(brandsData.data[0].id ?? 0);
    setBrandId(newBrandId);
  }, [brandsData, isBrandsError, organizationId, shouldSwitchToBrand]);

  const fillPostMutation = useMutation({
    mutationFn: (postId: number) => socialAPI.posts.findOne(currentBrandId ?? 0, organizationId ?? 0, postId),
    onSuccess(data) {
      setDetailPost(data.data);
    },
  });

  const {
    data: oneBrandData,
    isFetching: isFetchingOneBrand,
    refetch: refetchOneBrand,
    isError: isOneBrandError,
  } = useQuery({
    queryKey: ['findOneBrand', organizationId, currentBrandId],
    queryFn: () => socialAPI.brands.findOne(currentBrandId ?? 0, organizationId ?? 0),
    enabled: !!currentBrandId && !!organizationId && enable,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isOneBrandError) {
      setCurrentBrand(undefined);
      return;
    }
    if (!oneBrandData) return;
    if (currentBrand && currentBrandId === previousFetchedBrand) {
      const oldAccounts = currentBrand.linkedSocialAccounts?.map((val) => val.profileUrl) || [];
      const currentAccounts = oneBrandData.data.linkedSocialAccounts?.map((val) => val.profileUrl) || [];
      const newAccounts = _.difference(currentAccounts, oldAccounts);
      const removedAccounts = _.difference(oldAccounts, currentAccounts);
      if (newAccounts.length) toast.success(t('social-connected', { count: newAccounts.length }));
      if (removedAccounts.length) toast.success(t('social-disconnected', { count: removedAccounts.length }));
    }
    setPreviousFetchedBrand(currentBrandId);
    setCurrentBrand(oneBrandData.data);
    localStorage.setItem(`${localStorageKeys.social.brandId}.${organizationId}`, `${currentBrandId}`);
  }, [oneBrandData, isOneBrandError]);

  function openDetailsDrawer(post: IPost) {
    setDetailPost(post);
    fillPostMutation.mutate(post.id);
    detailDrawerHandler.onTrue();
  }

  return (
    <SocialContext.Provider
      value={{
        currentBrand,
        setBrandId,
        currentBrandId,
        brands,
        drawerHandler,
        drawerInitialValues,
        setDrawerInitialValues,
        refetchAllBrandsAndSet: (toSetBrand?: number) => {
          if (toSetBrand) setShouldSwitchToBrand(toSetBrand);
          refetchAllBrands();
        },
        refetchOneBrand: () => {
          if (currentBrandId) refetchOneBrand();
        },
        isFetchingBrands,
        isFetchingBrandsSuccess: isBrandsSuccess,
        isFetchingOneBrand,
        openDetailsDrawer,
        detailDrawerHandler,
        refetchDetailsPost: (postId: number) => fillPostMutation.mutate(postId),
        detailPost,

        // Internal method to enable queries
        enableContext,
      }}
    >
      {children}
    </SocialContext.Provider>
  );
};

export const useSocial = () => {
  const context = useContext(SocialContext);
  if (context === undefined) {
    throw new Error('useSocialContext must be used within a LocationDrawerContextProviced');
  }

  // Enable queries on first use
  useEffect(() => {
    context.enableContext();
  }, [context]);

  return context;
};
